import axios from "axios";
import api from "../../api/api";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import NaijaStates from "naija-state-local-government";
import useDetails from "../../hooks/useDetails";
import { getCurrentUser } from "../../utils/authService";
import avatar from "../../assets/images/user.png";
import { LuArrowLeft, LuArrowUpRight } from "react-icons/lu";
import moment from "moment";
import { useForm } from "react-hook-form";

const WORLD_CITIES_URL =
  "https://pkgstore.datahub.io/core/world-cities/world-cities_json/data/5b3dd46ad10990bca47b04b4739a02ba/world-cities_json.json";

const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg"];
const MAX_FILE_MB = 5;
const LOCATION_FIELDS = ["country_origin", "state_origin", "lga_origin"];

const inputClass = "form-control text-black font-semibold";
const labelClass = "text-sm font-medium text-gray-600";

// Make sure the saved value is always selectable, even if the option list
// hasn't loaded yet (e.g. the world-cities request is still in flight).
const withCurrent = (list, value) =>
  value && !list.includes(value) ? [value, ...list] : list;

const ReadOnlyField = ({ label, value }) => (
  <div className="w-full">
    <label className={labelClass}>{label}</label>
    <input type="text" value={value ?? ""} className={inputClass} disabled readOnly />
  </div>
);

// Backend stores day / month / year separately -> build a yyyy-MM-dd string
const buildDateOfBirth = (d) => {
  if (!d?.yob || !d?.mob || !d?.dob) return "";
  return `${d.yob}-${String(d.mob).padStart(2, "0")}-${String(d.dob).padStart(2, "0")}`;
};

function UpdateBioData() {
  const [worldCities, setWorldCities] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();
  const { staging: stage, data: detailsData } = useDetails();

  const {
    register,
    handleSubmit,
    reset,
    resetField,
    setValue,
    getValues,
    watch,
    formState: { dirtyFields, isSubmitting },
  } = useForm({
    defaultValues: {
      email: "",
      phone: "",
      dateOfBirth: "",
      marital_status: "",
      religion: "",
      country_origin: "",
      state_origin: "",
      lga_origin: "",
      photo: undefined,
    },
  });

  // ---------- load reference data ----------
  useEffect(() => {
    axios
      .get(WORLD_CITIES_URL)
      .then((res) => setWorldCities(res.data))
      .catch((err) => console.log(err));
  }, []);

  // ---------- populate form once details arrive ----------
  // reset() makes these the new "default" values, which is what
  // dirtyFields is measured against.
  useEffect(() => {
    if (!detailsData) return;
    reset({
      email: detailsData.email ?? "",
      phone: detailsData.phone ?? "",
      dateOfBirth: buildDateOfBirth(detailsData),
      marital_status: detailsData.marital_status ?? "",
      religion: detailsData.religion ?? "",
      country_origin: detailsData.country_origin ?? "",
      state_origin: detailsData.state_origin ?? "",
      lga_origin: detailsData.lga_origin ?? "",
      photo: undefined,
    });
  }, [detailsData, reset]);

  // ---------- dependent country / state / LGA options ----------
  const selectedCountry = watch("country_origin");
  const selectedState = watch("state_origin");

  const countries = useMemo(
    () => withCurrent([...new Set(worldCities.map((c) => c.country))].sort(), selectedCountry),
    [worldCities, selectedCountry]
  );

  const states = useMemo(() => {
    if (!selectedCountry) return [];
    const list =
      selectedCountry === "Nigeria"
        ? NaijaStates.states()
        : [
            ...new Set(
              worldCities
                .filter((c) => c.country === selectedCountry)
                .map((c) => c.subcountry)
            ),
          ].sort();
    return withCurrent(list, selectedState);
  }, [worldCities, selectedCountry, selectedState]);

  const lgaValue = watch("lga_origin");
  const cities = useMemo(() => {
    if (!selectedCountry || !selectedState) return [];
    const list =
      selectedCountry === "Nigeria"
        ? NaijaStates.lgas(String(selectedState))?.lgas ?? []
        : [
            ...new Set(
              worldCities
                .filter((c) => c.subcountry === selectedState)
                .map((c) => c.name)
            ),
          ];
    return withCurrent(list, lgaValue);
  }, [worldCities, selectedCountry, selectedState, lgaValue]);

  // ---------- passport photo ----------
  const photoField = register("photo", {
    onChange: (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        alert("Invalid file type. Please upload a valid file type");
        resetField("photo");
        e.target.value = "";
        return;
      }
      if (file.size / 1024 / 1024 > MAX_FILE_MB) {
        alert(`Please upload an image lower than ${MAX_FILE_MB}MB`);
        resetField("photo");
        e.target.value = "";
        return;
      }
      setPreviewUrl(URL.createObjectURL(file));
    },
  });
  const photoInputRef = useRef(null);

  // free the blob URL when it's replaced / on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // ---------- submit: only dirty fields ----------
  const onSubmit = async (values) => {
    const changedKeys = Object.keys(dirtyFields).filter((key) => {
      if (!dirtyFields[key]) return false;
      // an emptied file input is "dirty" vs undefined, but has nothing to send
      if (key === "photo") return Boolean(values.photo?.[0]);
      return true;
    });

    if (changedKeys.length === 0) {
      toast.info("No changes to save");
      return;
    }

    // country -> state -> LGA are dependent; if any changed, all must be set
    const locationChanged = changedKeys.some((k) => LOCATION_FIELDS.includes(k));
    if (locationChanged && LOCATION_FIELDS.some((k) => !values[k])) {
      toast.error("Please select country, state and LGA");
      return;
    }

    const formData = new FormData();
    changedKeys.forEach((key) => {
      if (key === "photo") {
        formData.append("photo", values.photo[0]);
      } else if (key === "dateOfBirth") {
        const dob = values.dateOfBirth ? moment(values.dateOfBirth) : null;
        formData.append("yob", dob ? dob.format("YYYY") : "");
        formData.append("mob", dob ? dob.format("MM") : "");
        formData.append("dob", dob ? dob.format("D") : "");
      } else {
        formData.append(key, values[key] ?? "");
      }
    });

    const toastId = toast.loading("Updating Biodata...");
    try {
      await api.patch(`/students/students/${user.role_id}/`, formData);
      toast.update(toastId, {
        render: "Update successful",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
      setPreviewUrl(null);
      // useDetails doesn't expose a refetch, so keep the original reload
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      const data = error?.response?.data;
      toast.update(toastId, {
        render: data ? JSON.stringify(data) : "Update failed. Try again!",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const hasChanges = Object.keys(dirtyFields).length > 0;

  return (
    <div>
      {detailsData && (
        <div className="space-y-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <LuArrowLeft
                className="size-6 text-brand-black cursor-pointer"
                onClick={() => navigate(-1)}
              />
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-brand-darkGray">Profile</h1>
                <p className="text-sm text-gray-400">View and edit profile information</p>
              </div>
            </div>
            <button
              type="button"
              className="bg-[#242D6D] py-2 px-3 flex items-center gap-9 border border-[#E2D49A80] rounded-xl hover:bg-[#242D6D]/95 hover:scale-105 transition-all ease-in-out"
              onClick={() => navigate("/result")}
            >
              <p className="text-sm font-medium text-white">View Results</p>
              <div className="size-9 bg-white rounded-lg flex justify-center items-center cursor-pointer group">
                <LuArrowUpRight className="size-[18px] text-[#242D6D] group-hover:scale-125 transition-all ease-in-out" />
              </div>
            </button>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="tab-content detail-list" id="pills-tabContent">
                <div className="tab-pane fade show active" id="general_detail">
                  <div className="row">
                    <div className="col-lg-12 mx-auto">
                      <div className="card">
                        <div className="card-body">
                          <form onSubmit={handleSubmit(onSubmit)} className="card-box">
                            {/* Passport photo */}
                            <div
                              className="w-full bg-[#FCFCFC] border border-[#EAECF0] rounded-lg flex flex-col md:flex-row md:items-center gap-7 p-5 cursor-pointer hover:bg-black/5"
                              onClick={() => photoInputRef.current?.click()}
                            >
                              <img
                                alt="passport"
                                className="size-[90px] rounded-full object-cover"
                                src={previewUrl || detailsData?.photo || avatar}
                              />
                              <div className="space-y-2">
                                <p className="text-xl font-semibold text-brand-darkGray">
                                  Student Photo
                                </p>
                                <p className="text-sm text-gray-400">
                                  Click here to edit your passport photo.
                                </p>
                              </div>
                            </div>

                            <input
                              aria-label="File input field"
                              className="sr-only"
                              type="file"
                              accept="image/png, image/jpg, image/jpeg"
                              {...photoField}
                              ref={(el) => {
                                photoField.ref(el);
                                photoInputRef.current = el;
                              }}
                            />

                            <div className="mt-4 space-y-5">
                              <div className="flex flex-col lg:flex-row gap-5">
                                <ReadOnlyField label="Title" value={detailsData?.title} />
                                <ReadOnlyField label="Last Name" value={detailsData?.surname} />
                                <ReadOnlyField label="First Name" value={detailsData?.othername} />
                              </div>

                              <div className="flex flex-col lg:flex-row gap-5">
                                <ReadOnlyField label="Matric Number" value={detailsData?.matric_number} />
                                <ReadOnlyField
                                  label="Application/Registration Number"
                                  value={detailsData?.application_no}
                                />
                                <ReadOnlyField label="Jamb Number" value={detailsData?.jamb_no} />
                              </div>

                              {/* Editable: contact */}
                              <div className="flex flex-col lg:flex-row gap-5">
                                <div className="w-full">
                                  <label className={labelClass}>Email</label>
                                  <input type="email" className={inputClass} {...register("email")} />
                                </div>
                                <div className="w-full">
                                  <label className={labelClass}>Phone Number</label>
                                  <input type="text" className={inputClass} {...register("phone")} />
                                </div>
                              </div>

                              {/* Editable: DOB / marital status */}
                              <div className="flex flex-col lg:flex-row gap-5">
                                <div className="w-full">
                                  <label className={labelClass}>Date of Birth</label>
                                  <input
                                    type="date"
                                    className={`${inputClass} capitalize`}
                                    {...register("dateOfBirth")}
                                  />
                                </div>
                                <ReadOnlyField label="Gender" value={detailsData?.gender} />
                                <div className="w-full">
                                  <label className={labelClass}>Marital Status</label>
                                  <select
                                    className={`${inputClass} capitalize`}
                                    {...register("marital_status")}
                                  >
                                    <option value="">--Select--</option>
                                    <option>Single</option>
                                    <option>Married</option>
                                    <option>Seperated</option>
                                  </select>
                                </div>
                              </div>

                              <p className="mt-5 text-sm font-medium text-gray-600">Academic Details</p>

                              <div className="flex flex-col lg:flex-row gap-5">
                                <ReadOnlyField label="Department" value={detailsData?.department} />
                                <ReadOnlyField label="Level of Study" value={`${detailsData?.level} Level`} />
                                <ReadOnlyField label="Academic Session" value={detailsData?.session?.name} />
                              </div>

                              <div className="flex flex-col lg:flex-row gap-5">
                                <ReadOnlyField label="School" value={detailsData?.faculty} />
                                <ReadOnlyField label="Entry Mode" value={detailsData?.admission_mode} />
                                <ReadOnlyField label="Entry Session" value={detailsData?.entry_session} />
                              </div>

                              <div className="flex flex-col lg:flex-row gap-5">
                                <div className="w-full">
                                  <label className={labelClass}>Religion</label>
                                  <select className={inputClass} {...register("religion")}>
                                    <option value="">--Select--</option>
                                    <option>Christianity</option>
                                    <option>Islam</option>
                                    <option>Others</option>
                                  </select>
                                </div>
                              </div>

                              <p className="mt-5 text-sm font-medium text-gray-600">Background</p>

                              <div className="flex flex-col gap-5">
                                <div className="w-full">
                                  <label className={labelClass}>Country</label>
                                  <select
                                    className={`${inputClass} capitalize`}
                                    {...register("country_origin", {
                                      onChange: () => {
                                        setValue("state_origin", "", { shouldDirty: true });
                                        setValue("lga_origin", "", { shouldDirty: true });
                                      },
                                    })}
                                  >
                                    <option value="">--Country--</option>
                                    {countries.map((item) => (
                                      <option value={item} key={item}>
                                        {item}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="w-full">
                                  <label className={labelClass}>State</label>
                                  <select
                                    className={`${inputClass} capitalize`}
                                    {...register("state_origin", {
                                      onChange: () =>
                                        setValue("lga_origin", "", { shouldDirty: true }),
                                    })}
                                  >
                                    <option value="">--State--</option>
                                    {states.map((item) => (
                                      <option value={item} key={item}>
                                        {item}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="w-full">
                                  <label className={labelClass}>LGA</label>
                                  <select
                                    className={`${inputClass} capitalize`}
                                    {...register("lga_origin")}
                                  >
                                    <option value="">--LGA--</option>
                                    {cities.map((item) => (
                                      <option value={item} key={item}>
                                        {item}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              <div className="flex justify-end">
                                <button
                                  className="bg-[#5D71C0] py-2 px-3 flex items-center gap-9 border border-[#E2D49A80] rounded-xl hover:bg-[#5D71C0]/95 hover:scale-105 transition-all ease-in-out disabled:opacity-60 disabled:hover:scale-100"
                                  type="submit"
                                  disabled={isSubmitting || !hasChanges}
                                >
                                  <p className="text-sm font-medium text-white">Update Profile</p>
                                  <div className="size-9 bg-[#1E2F733B] rounded-lg flex justify-center items-center cursor-pointer group">
                                    <LuArrowUpRight className="size-[18px] text-white group-hover:scale-125 transition-all ease-in-out" />
                                  </div>
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateBioData;